<script setup>
import { computed, ref } from 'vue';

const props = defineProps({ data: { type: Object, default: () => ({}) } });
const selectedOrigin = ref('ai');
const origins = [
  { value: 'ai', label: 'IA' },
  { value: 'human', label: 'Humano' },
  { value: 'mixed', label: 'IA + humano' },
  { value: 'unknown', label: 'Origem desconhecida' },
];
const definitions = [
  {
    key: 'offer_accepted',
    label: 'Ofertas aceitas',
    icon: 'i-lucide-handshake',
  },
  {
    key: 'contract_signed',
    label: 'Contratos assinados',
    icon: 'i-lucide-file-check-2',
  },
  { key: 'paid', label: 'Valor pago', icon: 'i-lucide-badge-dollar-sign' },
];
const cards = computed(() =>
  definitions.map(definition => {
    const stage = props.data.stages?.find(
      item => item.event_type === definition.key
    );
    const origin = stage?.origins?.find(
      item => item.attribution === selectedOrigin.value
    );
    return {
      ...definition,
      count: origin?.count || 0,
      amount: origin?.amount || 0,
      unidentified: stage?.unidentified_count || 0,
    };
  })
);
const recent = computed(() =>
  (props.data.recent_events || []).filter(
    event => event.attribution === selectedOrigin.value
  )
);
const currency = value =>
  Number(value || 0).toLocaleString('pt-BR', {
    style: 'currency',
    currency: 'BRL',
  });
const number = value => Number(value || 0).toLocaleString('pt-BR');
const eventLabel = value =>
  definitions.find(item => item.key === value)?.label || value;
const occurredAt = value => new Date(value).toLocaleString('pt-BR');
</script>

<template>
  <!-- eslint-disable vue/no-bare-strings-in-template, @intlify/vue-i18n/no-raw-text -->
  <section
    class="rounded-2xl border border-n-weak bg-n-solid-1 p-5"
    aria-labelledby="conversion-metrics-title"
  >
    <h2
      id="conversion-metrics-title"
      class="mb-1 text-base font-semibold text-n-slate-12"
    >
      Conversões por participação
    </h2>
    <p class="mb-4 text-sm text-n-slate-11">
      Acompanhe cada etapa por ações registradas. IA + humano indica
      participação dos dois. O histórico anterior à ativação não foi
      reclassificado.
    </p>
    <div
      class="mb-4 flex flex-wrap gap-2"
      role="group"
      aria-label="Participação na conversão"
    >
      <button
        v-for="origin in origins"
        :key="origin.value"
        type="button"
        class="rounded-lg border px-3 py-2 text-xs font-medium transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-n-brand"
        :class="
          selectedOrigin === origin.value
            ? 'border-n-brand bg-n-brand/10 text-n-brand'
            : 'border-n-weak text-n-slate-11 hover:bg-n-alpha-2'
        "
        :aria-pressed="selectedOrigin === origin.value"
        @click="selectedOrigin = origin.value"
      >
        {{ origin.label }}
      </button>
    </div>
    <div class="grid gap-3 md:grid-cols-3">
      <article
        v-for="card in cards"
        :key="card.key"
        class="min-w-0 rounded-xl border border-n-weak bg-n-alpha-1 p-4"
      >
        <div class="mb-2 flex items-center justify-between gap-2">
          <p class="mb-0 text-sm text-n-slate-11">{{ card.label }}</p>
          <span
            :class="card.icon"
            class="size-4 shrink-0 text-n-slate-10"
            aria-hidden="true"
          />
        </div>
        <strong
          class="block break-words text-2xl font-semibold"
          :class="card.count ? 'text-n-slate-12' : 'text-n-slate-11'"
        >
          {{ card.key === 'paid' ? currency(card.amount) : number(card.count) }}
        </strong>
        <p class="mb-0 mt-1 text-xs text-n-slate-11">
          {{ number(card.count) }} CPFs distintos nesta etapa
        </p>
        <p v-if="card.unidentified" class="mb-0 mt-2 text-xs text-n-slate-11">
          {{ number(card.unidentified) }} evento(s) sem CPF válido, fora dos
          totais.
        </p>
      </article>
    </div>
    <p class="mb-0 mt-3 text-xs text-n-slate-11">
      Cada CPF conta uma vez por etapa no período; em duplicidades, vale o maior
      valor. Assinatura e encerramento não comprovam pagamento.
    </p>
    <details class="mt-4 border-t border-n-weak pt-3">
      <summary class="cursor-pointer text-sm font-medium text-n-slate-12">
        Ver evidências recentes
      </summary>
      <p class="mt-2 text-xs text-n-slate-11">
        Últimos 15 eventos do período, filtrados pela participação selecionada.
      </p>
      <ul class="m-0 list-none divide-y divide-n-weak p-0">
        <li
          v-for="event in recent"
          :key="event.id"
          class="flex flex-wrap items-center justify-between gap-2 py-2 text-xs text-n-slate-11"
        >
          <span
            >{{ eventLabel(event.event_type) }} · Proposta #{{
              event.proposal_id
            }}</span
          >
          <span
            >{{ occurredAt(event.occurred_at) }} ·
            {{ currency(event.amount) }}</span
          >
        </li>
      </ul>
      <p v-if="!recent.length" class="mb-0 text-xs text-n-slate-11">
        Nenhum evento registrado para esta participação no período.
      </p>
    </details>
  </section>
</template>
